/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentProps, useMemo } from 'react';

import { Modal } from './Modal';
import { Drawer } from './Drawer';
import { SideSheet } from './SideSheet';
import { Popover } from './PopOver';

import { useIsBreakpoint } from 'hooks';

import { cn, ResponsiveBreakpoint } from 'styles';

import { OverlayCommonProps } from './types';

const overlays = {
  modal: Modal,
  drawer: Drawer,
  sheet: SideSheet,
  'pop-over': Popover,
};

type Overlays = typeof overlays;

export type OverlayOption = keyof Overlays;

// add responsive/big only prop

export type OverlaySpecificProps<Overlay extends OverlayOption> = Omit<
  ComponentProps<Overlays[Overlay]>,
  keyof OverlayCommonProps
>;

type CommonTargetAbleProps = Pick<
  OverlayCommonProps,
  'allowEscape' | 'title' | 'description' | 'showCloseButton' | 'gap'
>;

type ResponsiveOverlayProps<
  Default extends OverlayOption,
  SwitchTo extends OverlayOption,
> = OverlayCommonProps & {
  /**
   * Which breakpoint should the overlay type change?
   */
  switchOn?: ResponsiveBreakpoint;

  /**
   * overlay on larger screens
   *
   * Default:  modal
   */
  default?: Default;

  /**
   * overlay on small screens
   *
   * Default: drawer
   */
  switchTo?: SwitchTo;

  /**
   * The styles applied **only to the responsive component**
   */
  switchToClassName?: string;

  /**
   * The styles applied **only to the large (default)** component
   */
  largeClassName?: string;

  /**
   * The overlay props only applied the **large (default)** component
   */
  largeProps?: CommonTargetAbleProps & OverlaySpecificProps<Default>;

  /**
   * The overlay props only applied the **responsive** component
   */
  switchToProps?: CommonTargetAbleProps & OverlaySpecificProps<SwitchTo>;
} & OverlaySpecificProps<Default> &
  OverlaySpecificProps<SwitchTo>;

/**
 *  Handle Overlays on different devices easily
 *   - Define the default overlay = modal is the default
 *   - Chose which overlay and which breakpoint should be the reference to change
 *
 *  Whenever the screen size passes that breakpoint, it automatically switches between the two
 *
 *  Properties are spread out to whichever overlay is rendered, allowing full control
 */
export function ResponsiveOverlay<
  Default extends OverlayOption = 'modal',
  SwitchTo extends OverlayOption = 'drawer',
>({
  switchOn = 'PHONE',
  switchTo = 'drawer' as SwitchTo,
  default: defaultOverlay = 'modal' as Default,
  largeClassName,
  largeProps = {} as CommonTargetAbleProps & OverlaySpecificProps<Default>,
  switchToClassName,
  switchToProps = {} as CommonTargetAbleProps & OverlaySpecificProps<SwitchTo>,
  ...props
}: ResponsiveOverlayProps<Default, SwitchTo>): JSX.Element {
  const passedBreakpoint = useIsBreakpoint(switchOn);

  const Component = useMemo(() => {
    return overlays[passedBreakpoint ? switchTo : defaultOverlay];
  }, [passedBreakpoint, switchTo, defaultOverlay]);

  if (!Component) return <></>;

  return (
    <Component
      // We know it works
      {...(props as any)}
      {...((passedBreakpoint ? switchToProps : largeProps) as any)}
      className={cn(props.className, passedBreakpoint ? switchToClassName : largeClassName)}
    />
  );
}
